// @ts-check
import React, { useCallback } from 'react';
import { connect } from 'react-redux';
import { useMutation } from 'react-query';
import { usePortalConfig } from 'shared/context/PortalConfig';
import api from 'utils/api';
import { decamelizeKeys } from 'humps';
import { TextField } from 'preferral-ui/components/formik/TextField';
import { PlainSelectField } from 'preferral-ui/components/formik/PlainSelectField';
import { Button } from 'preferral-ui/components/Button';
import { FormStatusErrors } from 'preferral-ui/components/formik/FormStatusErrors';
import { Formik } from 'formik';
import FlashMessageActions from 'shared/actions/FlashMessageActions';

const otherOption = {
  value: 'other',
  label: 'Other (Explain)',
};

const blankOption = {
  value: '',
  label: 'Select...',
};

/** @typedef {{
  rejectionReason
}} MutationInput */

/** @typedef {{
  referralId: string;
  onSuccess(): void;
}} RejectReferralFormProps */

/** @type { React.FC<RejectReferralFormProps & { referralRejectionReasons: string[] | null }> } */
const _RejectReferralForm = (props) => {
  const { referralId, onSuccess, referralRejectionReasons } = props;

  const { apiPrefix } = usePortalConfig();

  const rejectionReasonOptions = [blankOption]
    .concat(
      (referralRejectionReasons || []).map((r) => ({ value: r, label: r }))
    )
    .concat([otherOption]);

  const mutation = useMutation((/** @type { MutationInput } */ rejectInput) =>
    api
      .post(
        `${apiPrefix}/referrals/${referralId}/rejections`,
        decamelizeKeys(rejectInput)
      )
      .then((resp) => resp.data)
  );

  const onSubmit = useCallback(
    (values, formikHelpers) => {
      const { setStatus, setSubmitting } = formikHelpers;

      setStatus({ errors: null });

      /** @type { MutationInput } */
      const input = {
        rejectionReason:
          !values.selectedReason || values.selectedReason === 'other'
            ? values.otherReason
            : values.selectedReason,
      };

      return mutation.mutateAsync(input).then(
        (_data) => {
          FlashMessageActions.flashSuccess({
            message: 'You have successfully rejected this referral',
          });
          return onSuccess();
        },
        (err) => {
          setSubmitting(false);
          if (err && err.errors && err.errors._error) {
            setStatus({
              errors: err.errors._error.map((e) => ({ key: '', message: e })),
            });
          } else {
            return Promise.reject(err);
          }
        }
      );
    },
    [onSuccess, mutation]
  );

  const hasOptions = rejectionReasonOptions.length > 2;

  return (
    <div className="_RejectReferralForm">
      <Formik
        initialValues={{
          selectedReason: '',
          otherReason: '',
        }}
        onSubmit={onSubmit}
        validate={
          hasOptions ? validateFormWithReasons : validateFormWithoutReasons
        }
      >
        {({ values, handleSubmit, isSubmitting, status }) => (
          <form onSubmit={handleSubmit}>
            <FormStatusErrors status={status} />

            {hasOptions ? (
              <>
                <div className="mt-3">
                  <PlainSelectField
                    name="selectedReason"
                    label="Rejection Reason"
                    options={rejectionReasonOptions}
                  />
                </div>
                {values.selectedReason === 'other' ? (
                  <div className="mt-3">
                    <TextField name="otherReason" label="Other Reason" />
                  </div>
                ) : null}
              </>
            ) : (
              <div className="mt-3">
                <TextField name="otherReason" label="Rejection Reason" />
              </div>
            )}

            <div className="mt-6 text-center">
              <Button
                type="submit"
                kind="primary"
                color="blue"
                isLoading={isSubmitting}
                disabled={isSubmitting}
              >
                Submit
              </Button>
            </div>
          </form>
        )}
      </Formik>
    </div>
  );
};

function validateFormWithReasons(values) {
  let errors = {};
  if (!values.selectedReason) {
    errors.selectedReason = 'Required';
  }
  if (values.selectedReason === 'other' && !values.otherReason) {
    errors.otherReason = 'Required';
  }
  return errors;
}

function validateFormWithoutReasons(values) {
  let errors = {};
  if (!values.otherReason) {
    errors.otherReason = 'Required';
  }
  return errors;
}

function mapStateToProps(state) {
  return {
    referralRejectionReasons:
      state.currentAgent && state.currentAgent.currentEmployment
        ? state.currentAgent.currentEmployment.organization.preferences
            .referralRejectionReasons
        : [],
  };
}

/** @type { React.FC<RejectReferralFormProps> } */
export const RejectReferralForm = connect(mapStateToProps)(_RejectReferralForm);
